import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/utils2";
import { getCalendarDateKey } from "../onsitemodule/eventDays";

const EVENT_COLORS = {
  current: "#2563eb",
  previous: "#f59e0b",
};

const number = (value) => Number(value || 0).toLocaleString("en-IN");

const percentDifference = (current, previous) => {
  if (!previous) return current ? null : 0;
  return ((current - previous) / previous) * 100;
};

const signedNumber = (value) => `${value > 0 ? "+" : ""}${number(value)}`;

const signedPercent = (value) => {
  if (value === null) return "N/A";
  return `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
};

const monthSerial = (month) => {
  const [year, monthNumber] = String(month).split("-").map(Number);
  return year * 12 + monthNumber - 1;
};

const eventMonthSerial = (eventStartDate) =>
  monthSerial(getCalendarDateKey(eventStartDate).slice(0, 7));

const monthName = (month) => {
  const [year, monthNumber] = String(month).split("-").map(Number);
  return new Intl.DateTimeFormat("en-IN", {
    month: "short",
    year: "numeric",
  }).format(new Date(year, monthNumber - 1, 1));
};

const relativeMonthLabel = (offset) => {
  if (offset === 0) return "Event month";
  if (offset < 0) {
    const months = Math.abs(offset);
    return `${months} month${months === 1 ? "" : "s"} before`;
  }
  return `${offset} month${offset === 1 ? "" : "s"} after`;
};

const combineMonthwise = (analytics, events) => {
  if (!analytics || !events) return [];

  const rows = new Map();

  ["current", "previous"].forEach((key) => {
    const startMonth = eventMonthSerial(events[key].eventStartDate);

    analytics[key].monthwise.forEach((item) => {
      const offset = monthSerial(item.month) - startMonth;
      const row = rows.get(offset) || {
        offset,
        period: relativeMonthLabel(offset),
        current: 0,
        previous: 0,
        currentMonth: "—",
        previousMonth: "—",
      };

      row[key] = item.count;
      row[`${key}Month`] = monthName(item.month);
      rows.set(offset, row);
    });
  });

  return [...rows.values()].sort((a, b) => a.offset - b.offset);
};

const combineDimension = (currentItems = [], previousItems = []) => {
  const rows = new Map();

  currentItems.forEach(({ name, count }) => {
    rows.set(name, { name, current: count, previous: 0 });
  });
  previousItems.forEach(({ name, count }) => {
    const row = rows.get(name) || { name, current: 0, previous: 0 };
    row.previous = count;
    rows.set(name, row);
  });

  return [...rows.values()].sort(
    (a, b) => b.current + b.previous - (a.current + a.previous)
  );
};

function SummaryCard({ label, value, helper, color }) {
  return (
    <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
      <CardContent>
        <Typography color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
        <Typography variant="h4" fontWeight={800} color={color} my={1}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {helper}
        </Typography>
      </CardContent>
    </Card>
  );
}

function ComparisonTable({ rows, totals, currentName, previousName }) {
  return (
    <TableContainer sx={{ maxHeight: 520 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>Category</TableCell>
            <TableCell align="right">{currentName}</TableCell>
            <TableCell align="right">Share</TableCell>
            <TableCell align="right">{previousName}</TableCell>
            <TableCell align="right">Share</TableCell>
            <TableCell align="right">Difference</TableCell>
            <TableCell align="right">Difference %</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const difference = row.current - row.previous;

            return (
              <TableRow key={row.name} hover>
                <TableCell component="th" scope="row">
                  {row.name}
                </TableCell>
                <TableCell align="right">{number(row.current)}</TableCell>
                <TableCell align="right">
                  {totals.current
                    ? `${((row.current / totals.current) * 100).toFixed(1)}%`
                    : "0.0%"}
                </TableCell>
                <TableCell align="right">{number(row.previous)}</TableCell>
                <TableCell align="right">
                  {totals.previous
                    ? `${((row.previous / totals.previous) * 100).toFixed(1)}%`
                    : "0.0%"}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color:
                      difference > 0
                        ? "success.main"
                        : difference < 0
                        ? "error.main"
                        : "text.primary",
                    fontWeight: 700,
                  }}
                >
                  {signedNumber(difference)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {signedPercent(
                    percentDifference(row.current, row.previous)
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DimensionComparison({ title, rows, totals, events }) {
  const chartRows = rows.slice(0, 10);

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Typography variant="h6" fontWeight={800}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Top categories by combined registrations; the table includes all
          categories.
        </Typography>

        <Box sx={{ width: "100%", height: Math.max(340, chartRows.length * 48) }}>
          <ResponsiveContainer>
            <BarChart
              data={chartRows}
              layout="vertical"
              margin={{ top: 5, right: 25, left: 30, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={150} />
              <Tooltip formatter={(value) => number(value)} />
              <Legend />
              <Bar
                dataKey="current"
                name={events.current.eventName}
                fill={EVENT_COLORS.current}
                radius={[0, 4, 4, 0]}
              />
              <Bar
                dataKey="previous"
                name={events.previous.eventName}
                fill={EVENT_COLORS.previous}
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>

      <ComparisonTable
        rows={rows}
        totals={totals}
        currentName={events.current.eventName}
        previousName={events.previous.eventName}
      />
    </Card>
  );
}

export default function RegistrationComparisonAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await axios.get(
        `${API_ENDPOINT}/admin/registration-comparison-analytics`,
        getHeader()
      );
      setData(response.data);
    } catch (requestError) {
      setData(null);
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to load registration analytics."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const monthwise = useMemo(
    () => combineMonthwise(data?.analytics, data?.events),
    [data]
  );
  const dimensions = useMemo(() => {
    if (!data) return [];

    return [
      {
        label: "Country",
        title: "Country-wise registrations",
        rows: combineDimension(
          data.analytics.current.countries,
          data.analytics.previous.countries
        ),
      },
      {
        label: "Industry sector",
        title: "Industry-sector registrations",
        rows: combineDimension(
          data.analytics.current.industrySectors,
          data.analytics.previous.industrySectors
        ),
      },
      {
        label: "Event discovery",
        title: "Event-discovery registrations",
        rows: combineDimension(
          data.analytics.current.eventDiscovery,
          data.analytics.previous.eventDiscovery
        ),
      },
    ];
  }, [data]);

  if (loading && !data) {
    return (
      <Box py={5}>
        <LinearProgress />
        <Typography mt={1} color="text.secondary">
          Loading registration comparison…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Stack spacing={2}>
        <Alert severity="error">{error}</Alert>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          sx={{ alignSelf: "flex-start" }}
        >
          Retry
        </Button>
      </Stack>
    );
  }

  if (!data) return null;

  const { events, analytics } = data;
  const totals = {
    current: analytics.current.total,
    previous: analytics.previous.total,
  };
  const difference = totals.current - totals.previous;
  const differenceRate = percentDifference(totals.current, totals.previous);
  const selectedDimension = dimensions[activeTab];

  return (
    <Box sx={{ minWidth: 0 }}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Box
        sx={{
          p: { xs: 2.5, md: 4 },
          mb: 3,
          borderRadius: 3,
          background: "linear-gradient(120deg, #0f172a, #1e3a8a)",
          color: "white",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="overline" sx={{ color: "#bfdbfe" }}>
              REGISTRATION ANALYTICS
            </Typography>
            <Typography variant="h4" fontWeight={800}>
              Registration comparison
            </Typography>
            <Typography sx={{ color: "#dbeafe", mt: 0.5 }}>
              {events.current.eventName} vs {events.previous.eventName}
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
            sx={{ alignSelf: { xs: "flex-start", md: "center" } }}
          >
            Refresh
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
          {["current", "previous"].map((key) => (
            <Chip
              key={key}
              label={events[key].eventName}
              sx={{
                bgcolor: EVENT_COLORS[key],
                color: key === "previous" ? "#172033" : "white",
                fontWeight: 700,
              }}
            />
          ))}
        </Stack>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        All four sections use the same portal-visitor definition. Blank country,
        industry-sector, or discovery values are grouped under “Not specified.”
      </Alert>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} lg={3}>
          <SummaryCard
            label={events.current.eventName}
            value={number(totals.current)}
            helper="Total visitor registrations"
            color={EVENT_COLORS.current}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <SummaryCard
            label={events.previous.eventName}
            value={number(totals.previous)}
            helper="Total visitor registrations"
            color={EVENT_COLORS.previous}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <SummaryCard
            label="Registration difference"
            value={signedNumber(difference)}
            helper={`${events.current.eventName} − ${events.previous.eventName}`}
            color={difference >= 0 ? "#15803d" : "#b91c1c"}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <SummaryCard
            label="Percentage difference"
            value={signedPercent(differenceRate)}
            helper={`${events.previous.eventName} is the baseline`}
            color={difference >= 0 ? "#15803d" : "#b91c1c"}
          />
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={800}>
            Month-wise registration pace
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Calendar months are aligned relative to each event month for a fair
            comparison.
          </Typography>

          <Box sx={{ width: "100%", height: 390 }}>
            <ResponsiveContainer>
              <BarChart
                data={monthwise}
                margin={{ top: 20, right: 20, left: 0, bottom: 65 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="period"
                  angle={-30}
                  textAnchor="end"
                  interval={0}
                  height={85}
                />
                <YAxis allowDecimals={false} />
                <Tooltip formatter={(value) => number(value)} />
                <Legend />
                <Bar
                  dataKey="current"
                  name={events.current.eventName}
                  fill={EVENT_COLORS.current}
                  radius={[5, 5, 0, 0]}
                />
                <Bar
                  dataKey="previous"
                  name={events.previous.eventName}
                  fill={EVENT_COLORS.previous}
                  radius={[5, 5, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Relative period</TableCell>
                <TableCell>{events.current.eventName} month</TableCell>
                <TableCell align="right">Registrations</TableCell>
                <TableCell>{events.previous.eventName} month</TableCell>
                <TableCell align="right">Registrations</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {monthwise.map((row) => (
                <TableRow key={row.offset}>
                  <TableCell>{row.period}</TableCell>
                  <TableCell>{row.currentMonth}</TableCell>
                  <TableCell align="right">{number(row.current)}</TableCell>
                  <TableCell>{row.previousMonth}</TableCell>
                  <TableCell align="right">{number(row.previous)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {dimensions.map(({ label }) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
      </Card>

      {selectedDimension && (
        <DimensionComparison
          title={selectedDimension.title}
          rows={selectedDimension.rows}
          totals={totals}
          events={events}
        />
      )}
    </Box>
  );
}
